import {Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Location} from '@angular/common';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';

import {TranslateParser, TranslateService} from '@ngx-translate/core';
import {ToastsManager} from "@app/shared/util/toastr.service";

import {ToolReturn} from '@models/tool-return.model';

import {Tool} from '@app/tool/model/tool.model';
import {ToolReturnsService} from '@services/tool-returns.service';

import {Log} from 'ng2-logger';
import {TreeModel} from "@components/tree/tree.model";
import {IssuesService} from "@services/issues.service";
import {IssueModalComponent, OperationsForReturnModalComponent, TicketPromoteModalComponent} from "@app/tool/interventions/components";
import {Issue} from "@models/issue.model";
import {Ticket} from "@models/ticket.model";
import {TicketService} from "@services/ticket.service";
import {ToolRequestStatus} from "@app/tool/model/enums/tool-request-status.enum";
import {ObservableService} from '@app/core/services/observable.service';
import {TicketCreateModalComponent} from '@app/roles/admin/pages/ticket-management/components';
import {TableConfig} from '@app/shared/util/table-config.utils';
import {TreeAdapter} from '@app/shared/util/tree-adapter.utils';
import {ToolReturnTypeEnum} from "@models/enums/tool-return-type.enum";
import {TicketType} from "@models/enums/ticket-type.enum";
import {
    MaintenaceTicketModalComponent
} from "@app/tool/interventions/components/tickets/maintenace-ticket-modal/maintenace-ticket-modal.component";
import {ToolSet} from "@app/tool/model/toolSet.model";
import {ConfirmationModalComponent} from "@app/shared/components";

export enum MaintenanceSelectedTools {
    AT_LINE_WITH_PLAN = "AT_LINE_WITH_PLAN",
    AT_LINE_WITHOUT_PLAN = "AT_LINE_WITHOUT_PLAN",
    AT_SERVICE = "AT_SERVICE"
}

@Component({
    selector: 'app-tool-returns-inspect',
    templateUrl: './tool-returns-inspect.component.html',
    styleUrls: ['./tool-returns-inspect.component.scss']
})
export class ToolReturnsInspectComponent implements OnInit {
    static returnTools: Array<Tool | ToolSet> = [];
    _logger = Log.create(ToolReturnsInspectComponent.name);

    @ViewChild(IssueModalComponent, {static: true}) issueModal: IssueModalComponent;
    @ViewChild(TicketPromoteModalComponent, {static: true}) ticketModal: TicketPromoteModalComponent;
    @ViewChild(MaintenaceTicketModalComponent, {static: true}) maintenanceTicketModal: MaintenaceTicketModalComponent;
    @ViewChild(OperationsForReturnModalComponent, {static: true}) operationsForReturnModalComponent: OperationsForReturnModalComponent;
    @ViewChild(TicketCreateModalComponent, {static: true}) createTicketModal: TicketCreateModalComponent;
    @ViewChild('errorModal', {static: true}) errorModal: ConfirmationModalComponent;

    confirmationPopupMessage: string;

    public toolReturn: ToolReturn = new ToolReturn();
    public hasSeveralReturnOptions: boolean;
    public tableConfig: TableConfig;
    public similarTickets: TreeModel[];

    inspectForm: UntypedFormGroup;
    showValidations = false;
    hasConfirmationInProgress = false
    isForMaintenance: boolean;
    isLoading: boolean;
    emptyMessage = 'atom.tool-returns.inspect-details.scanner.emptyText';
    issue: Issue;

    maintenanceToolsAtLineWithPlan: Tool[];
    maintenanceToolsAtLineWithoutPlan: Tool[];
    maintenanceToolsAtService: Tool[];

    public MaintenanceSelectedTools = MaintenanceSelectedTools;
    maintenanceSelectedTools: MaintenanceSelectedTools;

    static scannedToolsValidator(control: UntypedFormControl): { [s: string]: boolean } {
        const nrOfToolsFromReturn =
            ToolReturnsInspectComponent.returnTools.reduce((sum, item) => {
                return sum + ('nrOfTools' in item ? Number(item.nrOfTools) : 1);
            }, 0);

        const nrOfToolsFromControls = control.value.filter(
            (item: TreeModel) => item.children.length === 0).length;

        if (!control.value.length || nrOfToolsFromControls < nrOfToolsFromReturn) {
            return {'allToolsInspected': true};
        }
        return null;
    }

    constructor(private toolReturnsService: ToolReturnsService,
                private ticketService: TicketService,
                private router: Router,
                private route: ActivatedRoute,
                private location: Location,
                private toastsManager: ToastsManager,
                private issueService: IssuesService,
                private translationService: TranslateService,
                private translateParser: TranslateParser,
                private toolsObservable: ObservableService,
                private treeAdapter: TreeAdapter) {
        this.hasSeveralReturnOptions = this.toolReturn.hasSeveralReturnOptions || false;
    }

    ngOnInit() {
        this.onFormInit();
        ToolReturnsInspectComponent.returnTools = [];
        this.isLoading = true;
        this.route.params.subscribe(params => {
            this.getToolReturn(params['id']);
        });
    }

    getToolReturn(id) {
        this.isLoading = true;
        this.toolReturnsService.getToolReturn(id).subscribe(
            (data) => {
                this._logger.info('ngOnInit:: API Returned data', data);
                if (!data || (data.status !== ToolRequestStatus.DELIVERED && data.status !== ToolRequestStatus.WAITING_FOR_APPROVAL)) {
                    this.router.navigate([`tool-returns-lines`]);
                    return;
                }

                this.toolReturn = data;
                if (this.toolReturn.reason && this.toolReturn.hasPriority) {

                    this.isForMaintenance = true;

                    this.maintenanceToolsAtService = this.toolReturn.returnTools;
                    this.maintenanceToolsAtLineWithoutPlan = this.computeToolWithoutMaintenancePlan(this.toolReturn.maintenanceAtLineTools);
                    this.maintenanceToolsAtLineWithPlan = this.computeToolWithMaintenancePlan(this.toolReturn.maintenanceAtLineTools);

                    if (this.toolReturn && (!this.toolReturn.returnTools || this.toolReturn.returnTools.length === 0)) {
                        this.toolsObservable.onToolLoading();
                        this.emptyMessage = 'atom.tool-returns.inspect-details.scanner.allToolsAtLine'
                    }
                } else {
                    ToolReturnsInspectComponent.returnTools = data.returnTools;
                }
                this.isLoading = false;
            },
            (error) => {
                this.isLoading = false;
                this._logger.info('ngOnInit:: API Returned error', error);
                this.router.navigate([`tool-returns-lines`]);
            }
        );
    }

    onFormInit() {
        this.inspectForm = new UntypedFormGroup({
            'scannedTools': new UntypedFormControl([], ToolReturnsInspectComponent.scannedToolsValidator.bind(this)),
        });

        this._logger.info('onFormInit:: Form initialised', this.inspectForm);
    }

    approveToolReturn() {
        if (this.inspectForm.invalid) {
            this._logger.info('approveToolReturn:: Form is invalid', this.inspectForm);
            this.showValidations = true;
            return;
        }
        this._logger.info("approveToolReturn:: Approving tool return");
        this.showValidations = false;
        this.hasConfirmationInProgress = true;
        this.toolReturnsService.approveToolReturn(this.toolReturn, false).subscribe(
            (data: any) => {
                this._logger.info("approveToolReturn:: API returned data", data);
                this.toastsManager.success(this.buildApproveMessage(this.toolReturn.id));
                this.router.navigate([`tool-returns-lines`]);
                this.hasConfirmationInProgress = false;
            },
            (error: any) => {
                this._logger.error("approveToolReturn:: API returned error", error);
                this.hasConfirmationInProgress = false;
            }
        );
    }

    approveToolReturnForService() {
        if (this.inspectForm.invalid && this.toolReturn.returnTools.length > 0) {
            this._logger.info('approveToolReturn:: Form is invalid', this.inspectForm);
            this.showValidations = true;
            return;
        }
        this.hasConfirmationInProgress = true;
        this._logger.info("approveToolReturn:: Approving tool return");
        this.showValidations = false;
        this.toolReturnsService.approveToolReturn(this.toolReturn, true).subscribe(
            (data: any) => {
                this._logger.info("approveToolReturn:: API returned data", data);
                this.toastsManager.success(this.buildApproveMessage(this.toolReturn.id));
                this.router.navigate([`tool-returns-lines`]);
                this.hasConfirmationInProgress = false;
            },
            (error: any) => {
                this._logger.error("approveToolReturn:: API returned error", error);
                this.hasConfirmationInProgress = false;
            }
        );
    }

    onToolScanned(tool) {
        this.toolReturnsService.confirmToolScan(this.toolReturn, tool).subscribe((data: any) => {
                tool.inspected = true;
                this._logger.info("confirmToolScan:: API returned data", data);
            },
            (error: any) => {
                this._logger.error("confirmToolScan:: API returned error", error);
            }
        );
    }

    onToolsScanned(tools) {
        this._logger.info("onToolsScanned:: Scanned tools", tools);
        this.inspectForm.controls['scannedTools'].setValue(tools);
    }

    onRowAlertClick(node: TreeModel) {
        this._logger.info("onRowAlertClick:: event", node);
        this.getIssueDetails(node.data);
    }

    onPromoteToTicket(issue: Issue) {
        this._logger.info("onPromoteToTicket:: Issue to be promoted", issue);
        this.issue = issue;
        this.isLoading = true;
        this.ticketService.getOperationsForReturnOrIssueTools(this.toolReturn.id, issue).subscribe(
            data => {
                this.issueModal.close();
                if (data.length > 0) {
                    this.operationsForReturnModalComponent.open(data, TicketType.Issue);
                } else {
                    this.ticketModal.open(issue);
                }
                this.isLoading = false;
            },
            error => {
                this._logger.error("getOperationsForReturnOrIssueTools:: API returned error", error);
                this.isLoading = false;
            }
        );
    }

    onReject(issue: Issue) {
        this._logger.info("onReject:: Issue to be rejected", issue);
        this.rejectIssue(issue);
    }

    rejectIssue(issue: Issue) {
        this.issueModal.close();
        this.hasConfirmationInProgress = false;
        this._logger.info("rejectIssue:: Rejecting issue", issue);
        this.issueService.rejectIssue(issue).subscribe(
            (data) => {
                this.hasConfirmationInProgress = true;
                this._logger.info("rejectIssue:: API returned data", data);
                this.toastsManager.info(this.translationService.instant('atom.tool-returns.toasts.rejectSuccess'));
                this.getToolReturn(this.toolReturn.id);
                this.issueModal.close();

            },
            (error) => {
                this.hasConfirmationInProgress = true;
                this._logger.info("rejectIssue:: API returned error", error);
                this.toastsManager.error(this.translationService.instant('atom.tool-returns.toasts.rejectFail'));
            }
        )
    }

    createTicket(ticket: Ticket) {
        switch (ticket.type) {
            case TicketType.Issue: {
                this.createIssueTicket(ticket);
                break;
            }
            case TicketType.FromReturn: {
                this.createTicketAndRemoveTools(ticket);
                break;
            }
        }

    }

    approveAndCreateMaintenanceTicket(maintenanceSelectedTools: MaintenanceSelectedTools) {
        if ((this.toolReturn && this.toolReturn.returnTools)) {
            this.isLoading = true;

            this.maintenanceSelectedTools = maintenanceSelectedTools;

            this.ticketService.getOperationsForReturnOrIssueTools(this.toolReturn.id, null).subscribe(
                data => {
                    if (data.length > 0) {
                        this.operationsForReturnModalComponent.open(data, TicketType.Maintenance);
                    } else {
                        this.openMaintenanceTicketModal();
                    }
                    this.isLoading = false;
                },
                error => {
                    this._logger.error("createTicket:: API returned error", error);
                    this.isLoading = false;
                }
            );
            return;
        }

        this._logger.info('approveToolReturn:: Form is invalid', this.inspectForm);
        this.showValidations = true;
    }

    createMaintenanceTicket(tickets: any) {
        this._logger.info("approveToolReturn:: Approving tool return for maintenance");

        this.isLoading = true;
        this.toolReturnsService.approveToolReturnForMaintenance(this.toolReturn.id, tickets).subscribe(
            () => {
                if (tickets.TicketAtLine && tickets.TicketAtLine.toolIds && tickets.TicketAtLine.toolIds.length > 0) {
                    this.toastsManager.success(this.translationService
                        .instant('admin.tickets.create-modal.maintenance.ticketedAtLineCreatedSuccess'));
                }

                if (tickets.TicketAtService && tickets.TicketAtService.toolIds && tickets.TicketAtService.toolIds.length > 0) {
                    this.toastsManager.success(this.translationService
                        .instant('admin.tickets.create-modal.maintenance.ticketedAtServiceCreatedSuccess'));
                }

                this.getToolReturn(this.toolReturn.id);
            },
            (error: any) => {
                this._logger.error("approveToolReturn:: API returned error", error);
                this.toastsManager.error(this.translationService
                    .instant('admin.tickets.create-modal.maintenance.ticketedCreatedFailed'));
                this.isLoading = false;
            }
        );
    }

    onOperationInfoApproved(data: any) {
        if (!this.issue) {
            this.openMaintenanceTicketModal(data.operations);
        } else {
            this.ticketModal.open(this.issue, data.operations);
        }
    }

    sendToolsToService() {
        if (this.toolReturn) {
            this.tableConfig = new TableConfig();
            this.tableConfig.upsertFilter("toolReturnId", this.toolReturn.id);
        }

        this.createTicketModal.open(this.toolReturn, TicketType.FromReturn);
    }

    checkForSimilarTickets(ticket) {
        this.isLoading = true;
        this.ticketService.checkForSimilarTickets(ticket).subscribe(
            (data: any) => {
                this._logger.info("checkForSimilarTickets:: API Returned data", data)
                this.similarTickets = this.treeAdapter.adaptTicketOverviewToTree(data, 'ticketId');
                this._logger.info("checkForSimilarTickets:: Tree data", this.similarTickets);
                this.isLoading = false;
            },
            (error: any) => {
                this.isLoading = false;
                this._logger.error("createTicket:: API Returned error", error);
            });
    }

    buildApproveMessage(toolReturnId) {
        return this.translationService.instant('atom.tool-returns.toasts.approved',
            {toolReturnId: toolReturnId});
    }

    buildCreateMessage(ticketId) {
        const message = this.translationService.instant('admin.tickets.toasts.created');
        return this.translateParser.interpolate(message, {ticketId: ticketId});
    }

    onItemNotFound(event: any) {
        this.toastsManager.warning(this.buildToolNotRequiredMessage(event));
    }

    onBackPressed() {
        this.location.back();
    }

    getLabel(toolReturn: ToolReturn) {
        return toolReturn.type !== ToolReturnTypeEnum.LINE ?
            'atom.tool-returns.inspect-details.display.reason' :
            'atom.tool-returns.inspect-details.display.lineInfo';
    }

    isModalOpen() {
        return this.createTicketModal.isOpen() || this.issueModal.isOpen() || this.operationsForReturnModalComponent.isOpen()
            || this.ticketModal.isOpen() || this.maintenanceTicketModal.isOpen();
    }

    private buildToolNotRequiredMessage(uid) {
        return this.translationService.instant('tree.toolSelect.toasts.notRequired', {uid: uid});
    }

    private createIssueTicket(ticket: Ticket) {
        this._logger.info("createNormalTicket:: Creating ticket", ticket);
        this.isLoading = true;
        ticket.lineToolRequestId = this.toolReturn.toolRequest.id;
        this.ticketService.createTicket(ticket).subscribe(
            (data: any) => {
                this._logger.info("createTicket:: API returned data", data);
                this.getToolReturn(this.toolReturn.id);
                this.toastsManager.success(this.buildCreateMessage(data));
                this.ticketModal.resetForm();
                this.ticketModal.close();
                this.isLoading = false;
            },
            (error: any) => {
                if (error && error.error) {
                    this.confirmationPopupMessage = error.error.message;
                    this.errorModal.open();
                } else {
                    this._logger.error("createIssueTicket:: API returned error", error);
                }
                this.isLoading = false;
            })
    }

    private createTicketAndRemoveTools(ticket: Ticket) {
        this.isLoading = true;

        ticket.lineToolRequestId = this.toolReturn.toolRequest.id;
        ticket.infoFromLine = this.toolReturn.reason;

        this.createTicketModal.close();
        this.ticketService.createTicketFromReturn(ticket, this.toolReturn.id).subscribe(
            (data: any) => {
                this._logger.info("createTicketAndRemoveTools:: API Returned data", data);
                this.getToolReturn(this.toolReturn.id);
            },
            (error: any) => {
                if (error && error.error) {
                    this.confirmationPopupMessage = error.error.message;
                    this.errorModal.open();
                }
                this._logger.error("createTicketAndRemoveTools:: API Returned error", error);
            }
        )
    }

    private getIssueDetails(nodeData: any) {
        this._logger.info("getIssueDetails:: Retrieving issue details for issueId", nodeData.issue.id);
        this.issueService.getIssueById(nodeData.issue.id).subscribe(
            (data) => {
                this._logger.info("getIssueDetails:: API Returned data", data);

                const toolIds = this.mapToolsToList(this.toolReturn.returnTools).map(item => item.id);
                const allToolsIncluded = data.tools.every(element => {
                    return toolIds.includes(element.id);
                });
                this.issueModal.open(data, nodeData.id, allToolsIncluded);
            },
            (error) => {
                this._logger.error("getIssueDetails:: API Returned error", error);
                this.toastsManager.error(this.translationService.instant('atom.tool-returns.toasts.couldNotOpenIssue'));
            }
        )
    }

    private openMaintenanceTicketModal(operations: any = []) {
        if (this.toolReturn) {
            this.tableConfig = new TableConfig();
            this.tableConfig.upsertFilter("toolReturnId", this.toolReturn.id);
        }
        let tools: Tool[];
        switch (this.maintenanceSelectedTools) {
            case  MaintenanceSelectedTools.AT_LINE_WITH_PLAN:
                tools = this.maintenanceToolsAtLineWithPlan
                break;
            case  MaintenanceSelectedTools.AT_LINE_WITHOUT_PLAN:
                tools = this.maintenanceToolsAtLineWithoutPlan;
                break;
            default:
                tools = [];
        }

        this.maintenanceTicketModal.open(this.toolReturn, tools, operations);
    }

    private mapToolsToList(tools: any[]): any[] {
        const result = [];
        tools.forEach(tool => {
            if (tool instanceof Tool) {
                result.push(tool);
            } else if (tool instanceof ToolSet) {
                tool.tools.forEach(toolInSet => {
                    result.push(toolInSet);
                });
            }
        });
        return result;
    }

    private computeToolWithMaintenancePlan(tools: any[]): any[] {
        const result = [];
        tools.forEach(tool => {
            if (tool instanceof Tool && tool.maintenancePlanId) {
                result.push(tool);
            } else if (tool instanceof ToolSet && tool.tools.find(item => item.maintenancePlanId)) {
                result.push(tool);
            }
        });
        return result;
    }

    private computeToolWithoutMaintenancePlan(tools: any[]): any[] {
        const result = [];
        tools.forEach(tool => {
            if (tool instanceof Tool && !tool.maintenancePlanId) {
                result.push(tool);
            } else if (tool instanceof ToolSet && !tool.tools.some(item => item.maintenancePlanId)) {
                result.push(tool);
            }
        });
        return result;
    }

    get isScanListenerActive() {
        return !this.isModalOpen() && !this.isForMaintenance;
    }
}
